// The thread page's read (REQ-2026-10-07-167): the shapes apps/api/src/threads/read.ts answers, a module of its own as
// notes/api.ts is (the portal's api.ts is shared by every track).
import { viewFetch as fetch } from '../view-as.js';
import { Unauthenticated } from '../api.js';
import { opensPath } from '../deals/live-words.js';
import type { ThreadSummary } from './SummaryLines.js';

export interface ThreadPerson { name: string | null; address: string | null; personId: string | null }

export interface ThreadMessage {
  id: string;
  at: string;
  kind: string;
  mine: boolean;
  outbound: boolean;
  from: ThreadPerson | null;
  to: ThreadPerson[];
  cc: ThreadPerson[];
  subject: string | null;
  words: string | null;
  cut: boolean;
  attachments: Array<{ name: string; sizeBytes: number | null }>;
  open: { page: 'item' | 'meeting' | 'chat'; id: string } | null;
}

export interface ThreadRead {
  anchorId: string;
  threadId: string | null;
  kind: string;
  title: string;
  messages: ThreadMessage[];
  more: number;
  firstAt: string;
  lastAt: string;
  companies: Array<{ id: string; name: string }>;
  /** Cobrain's two lines on it (B2355); absent or null where there are none. */
  summary?: ThreadSummary | null;
}

/** Null where the reader may not read it, or it does not exist: the page says the one thing for both. */
export async function readThread(store: string, id: string): Promise<ThreadRead | null> {
  const r = await fetch(`/api/threads/${encodeURIComponent(id)}?${new URLSearchParams({ store }).toString()}`, { credentials: 'same-origin' });
  if (r.status === 401) throw new Unauthenticated();
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return ((await r.json()) as { thread: ThreadRead }).thread;
}

/** Where a written conversation opens: the thread page, in the store it came from. */
export function threadPath(signalId: string, store: string): string {
  return `/threads/${encodeURIComponent(signalId)}?store=${encodeURIComponent(store)}`;
}

/** The kinds a thread page reads: written messages. A meeting, a call or an article has a page of its own or none. */
const WRITTEN = new Set(['email', 'teams_chat', 'teams_channel', 'whatsapp', 'cobrain_chat', 'manual_note', 'note']);

/**
 * Where a conversation, or a press and intelligence item drawn from one, opens (REQ-2026-10-07-167): a meeting or a chat
 * on its own page, as search opens them; any other written message on the thread page; anything else (an article with
 * no address, a calendar entry) where a page opens it, else nowhere.
 */
export function conversationPath(signalId: string, open: { page: 'item' | 'meeting' | 'chat'; id: string } | null, store: string, kind?: string): string | null {
  if (open && open.page !== 'item') return opensPath(signalId, open, store);
  if (kind && !WRITTEN.has(kind)) return open ? opensPath(signalId, open, store) : null;
  return threadPath(signalId, store);
}
